import { useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Desktop-only fullscreen viewer for the "Proof, Not Promises" tiles.
// Shows the full, uncropped image (object-contain) with the tile's
// caption anchored at the bottom, and left/right arrows (+ keyboard) to
// step through all tiles.
export const BushCabinProofLightbox = ({ points, index, onClose, onNavigate }) => {
  const count = points.length;

  const goPrev = useCallback(() => onNavigate((index - 1 + count) % count), [index, count, onNavigate]);
  const goNext = useCallback(() => onNavigate((index + 1) % count), [index, count, onNavigate]);

  useEffect(() => {
    if (index === null) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [index, onClose, goPrev, goNext]);

  if (index === null) return null;
  const p = points[index];
  const Icon = p.icon;

  return (
    <div
      data-testid="bushcabin-lightbox"
      className="hidden md:flex fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm items-center justify-center"
      onClick={onClose}
    >
      <button
        data-testid="bushcabin-lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-20"
      >
        <X size={32} strokeWidth={1.5} />
      </button>

      <button
        data-testid="bushcabin-lightbox-prev"
        onClick={(e) => { e.stopPropagation(); goPrev(); }}
        aria-label="Previous"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[var(--brand-primary)] transition-colors z-20 p-2"
      >
        <ChevronLeft size={44} strokeWidth={1.2} />
      </button>
      <button
        data-testid="bushcabin-lightbox-next"
        onClick={(e) => { e.stopPropagation(); goNext(); }}
        aria-label="Next"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-[var(--brand-primary)] transition-colors z-20 p-2"
      >
        <ChevronRight size={44} strokeWidth={1.2} />
      </button>

      <div
        className="relative w-full h-full flex flex-col items-center justify-center px-6 md:px-24 py-10"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          data-testid="bushcabin-lightbox-image"
          src={p.img}
          alt={p.t}
          className="max-w-full max-h-[70vh] object-contain"
        />
        <div data-testid="bushcabin-lightbox-caption" className="max-w-2xl text-center mt-8">
          <Icon size={24} className="text-[var(--brand-primary)] mx-auto mb-4" strokeWidth={1.3} />
          <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{p.t}</h3>
          <p className="text-white/80 mt-3 leading-relaxed">{p.d}</p>
          <p className="font-mono text-xs text-white/40 tracking-[0.2em] mt-6">{index + 1} / {count}</p>
        </div>
      </div>
    </div>
  );
};
